import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosConfig';
import { formatCurrency } from '../utils/currency';
import { formatDate } from '../utils/dates';
import './Products.css';
import './Orders.css';

const STATUS_LABEL = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  dispatched: 'Dispatched',
  invoiced: 'Invoiced'
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [printNote, setPrintNote] = useState(null);

  const load = async () => {
    try {
      const response = await axios.get('/api/orders');
      setOrders(response.data.orders || []);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to load field orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!printNote) return undefined;
    document.body.classList.add('delivery-note-print-active');
    const timer = setTimeout(() => window.print(), 150);
    return () => {
      clearTimeout(timer);
      document.body.classList.remove('delivery-note-print-active');
    };
  }, [printNote]);

  const refreshSelected = (order) => {
    setSelected(order);
    setOrders((prev) => prev.map((row) => (row.id === order.id ? order : row)));
  };

  const act = async (path) => {
    if (!selected) return;
    setBusy(true);
    try {
      const response = await axios.post(path);
      refreshSelected(response.data.order);
    } catch (error) {
      alert(error.response?.data?.message || 'Could not update the order');
    } finally {
      setBusy(false);
    }
  };

  const createInvoice = async () => {
    if (!selected) return;
    if (!window.confirm(`Create an invoice for ${selected.order_number} and deduct stock?`)) return;
    setBusy(true);
    try {
      const invoiceRes = await axios.post('/api/invoices', {
        customer_id: selected.customer_id,
        issue_date: new Date().toISOString().slice(0, 10),
        status: 'pending',
        notes: `Field order ${selected.order_number}${selected.delivery_note_number ? ` · ${selected.delivery_note_number}` : ''}`,
        items: (selected.items || []).map((item) => ({
          product_id: item.product_id,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          uom_id: item.uom_id || undefined,
          discount: 0,
          gst_rate: parseFloat(item.gst_rate) || 0,
          gst_inclusive: Boolean(Number(item.gst_inclusive))
        }))
      });
      const invoice = invoiceRes.data.invoice;
      const linked = await axios.post(`/api/orders/${selected.id}/invoice`, { invoice_id: invoice.id });
      refreshSelected(linked.data.order);
      alert(`Invoice ${invoice.invoice_number || ''} created. Stock is updated.`);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to create the invoice');
    } finally {
      setBusy(false);
    }
  };

  const lineTotal = (item) => (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);

  return (
    <div className="page-container">
      <div className="container orders-page">
        <div className="page-header">
          <h1>Field orders</h1>
        </div>
        <p className="orders-lead">
          Orders taken by salesmen in the market. Approve, dispatch a delivery note, then create the invoice. Stock is deducted only when the invoice is created.
        </p>

        {loading ? (
          <div className="card">Loading orders…</div>
        ) : (
          <div className="orders-layout">
            <div className="card">
              <table className="table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Customer</th>
                    <th>Salesman</th>
                    <th>Status</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 ? (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center' }}>No field orders yet</td>
                    </tr>
                  ) : (
                    orders.map((order) => (
                      <tr
                        key={order.id}
                        className={selected?.id === order.id ? 'orders-row-active' : ''}
                        onClick={() => setSelected(order)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>
                          <strong>{order.order_number}</strong>
                          <div className="orders-muted">{formatDate(order.order_date)}</div>
                        </td>
                        <td>
                          {order.customer_name || '—'}
                          <div className="orders-muted">{order.customer_phone || ''}</div>
                        </td>
                        <td>{order.salesman_name || '—'}</td>
                        <td>
                          <span className={`status-badge status-${order.status}`}>
                            {STATUS_LABEL[order.status] || order.status}
                          </span>
                        </td>
                        <td>{formatCurrency(order.total)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="card orders-detail">
              {!selected ? (
                <p className="orders-muted">Select an order to approve, dispatch, or invoice it.</p>
              ) : (
                <>
                  <div className="orders-detail-head">
                    <div>
                      <h2>{selected.order_number}</h2>
                      <p className="orders-muted">{formatDate(selected.order_date)}</p>
                    </div>
                    <span className={`status-badge status-${selected.status}`}>
                      {STATUS_LABEL[selected.status] || selected.status}
                    </span>
                  </div>
                  <p>
                    <strong>{selected.customer_name}</strong>
                    {selected.customer_phone ? ` · ${selected.customer_phone}` : ''}
                  </p>
                  {selected.customer_address ? <p className="orders-muted">{selected.customer_address}</p> : null}
                  <p className="orders-muted">Taken by {selected.salesman_name || '—'}</p>
                  {selected.notes ? <p>{selected.notes}</p> : null}
                  {selected.delivery_note_number ? (
                    <p>Delivery note <strong>{selected.delivery_note_number}</strong></p>
                  ) : null}

                  <table className="table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Qty</th>
                        <th>Price</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selected.items || []).map((item) => (
                        <tr key={item.id}>
                          <td>{item.description}</td>
                          <td>
                            {item.quantity}
                            {item.uom_code ? ` ${item.uom_code}` : ''}
                          </td>
                          <td>{formatCurrency(item.unit_price)}</td>
                          <td>{formatCurrency(lineTotal(item))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="orders-total">Total {formatCurrency(selected.total)}</p>

                  <div className="orders-actions">
                    {selected.status === 'pending' && (
                      <>
                        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => act(`/api/orders/${selected.id}/approve`)}>
                          Approve
                        </button>
                        <button type="button" className="btn btn-danger" disabled={busy} onClick={() => act(`/api/orders/${selected.id}/reject`)}>
                          Reject
                        </button>
                      </>
                    )}
                    {selected.status === 'approved' && (
                      <button type="button" className="btn btn-primary" disabled={busy} onClick={() => act(`/api/orders/${selected.id}/dispatch`)}>
                        Dispatch
                      </button>
                    )}
                    {(selected.status === 'dispatched' || selected.status === 'invoiced') && selected.delivery_note_number && (
                      <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setPrintNote(selected)}>
                        Print delivery note
                      </button>
                    )}
                    {selected.status === 'dispatched' && (
                      <button type="button" className="btn btn-primary" disabled={busy} onClick={createInvoice}>
                        Create invoice
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {printNote && (
        <div className="delivery-note-print">
          <h1>Delivery note</h1>
          <p><strong>{printNote.delivery_note_number}</strong></p>
          <p>Order {printNote.order_number} · {formatDate(printNote.order_date)}</p>
          <p>
            {printNote.customer_name}
            {printNote.customer_phone ? ` · ${printNote.customer_phone}` : ''}
          </p>
          {printNote.customer_address ? <p>{printNote.customer_address}</p> : null}
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Qty</th>
              </tr>
            </thead>
            <tbody>
              {(printNote.items || []).map((item) => (
                <tr key={item.id}>
                  <td>{item.description}</td>
                  <td>{item.quantity}{item.uom_code ? ` ${item.uom_code}` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p>Received by ______________________</p>
          <button type="button" className="btn btn-secondary no-print" onClick={() => setPrintNote(null)}>
            Close
          </button>
        </div>
      )}
    </div>
  );
};

export default Orders;
